/**
 * SUPPLEMENTA: Fork von assets/sticky-scroll-direction.js.
 *
 * Die Vendor-Fassung liest die Header-Hoehe einmal beim Start und nur, wenn ein
 * <store-header data-is-sticky="true"> im Dokument steht. Der Supplementa-Header
 * ist kein store-header, also faende sie dort nichts und die sticky Spalte
 * dockte 48px unter dem Seitenrand — quer durch den Header.
 *
 * Diese Fassung liest die Hoehe bei jedem Zugriff aus --sup-header-height und
 * folgt zusaetzlich --sup-header-offset: dem Streifen, den der Header gerade
 * tatsaechlich verdeckt. Scrollt er aus dem Bild, ist der Wert 0 und die Spalte
 * holt sich den Platz zurueck, statt ihn fuer einen unsichtbaren Header frei zu
 * halten. Dazu kommt die Behandlung von Drawern und Modals, die die Seite mit
 * position: fixed am Body sperren.
 *
 * Warum ein Fork und kein Hook: die Aenderungen liegen mitten in der Geometrie
 * der Komponente, ueber acht Stellen verteilt. Ein Hook waere hier kein Aufruf,
 * sondern ein zweites Mal dieselbe Rechnung.
 *
 * Geladen wird diese Datei statt der Vendor-Datei — in sections/main-product.liquid
 * und snippets/facet-filters.liquid, beide als markierter Hook. Der Custom-Element-
 * Name bleibt sticky-scroll-direction, damit das Vendor-Markup unveraendert bleibt.
 *
 * Bei einem Theme-Update pruefen, was sich am Original getan hat:
 * git diff enterprise/<alt> enterprise/<neu> -- assets/sticky-scroll-direction.js
 */

/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Adjusts the css top property of an element such that it sticks appropriately based on the scroll
 * direction. The container is assumed to be position: sticky, with top: 0 (or whatever). When
 * scrolling down, it'll stick to the bottom of the container, when scrolling up it'll stick to
 * the top of the container.
 */
if (!customElements.get('sticky-scroll-direction')) {
  class StickyScrollDirection extends HTMLElement {
    connectedCallback() {
      this.init();
    }

    init() {
      this.container = this.firstElementChild;
      this.currentTop = Number.parseInt(
        document.documentElement.scrollTop + this.getBoundingClientRect().top - this.offsetTop,
        10
      );
      this.scrollY = window.scrollY;
      this.minStickySize = this.dataset.minStickySize;

      this.toggleListeners();
      window.addEventListener('on:breakpoint-change', this.toggleListeners.bind(this));
    }

    /**
     * Height of the header that overlays the page while scrolling, or 0 if none
     * does. Read on demand rather than once, because a custom header publishes
     * its height only after it has initialised and the value changes with the
     * breakpoint.
     * @param {CSSStyleDeclaration} [rootStyle] Already computed root styles, so
     *   callers needing several custom properties can avoid a second lookup.
     * @returns {number}
     */
    getHeaderHeight(rootStyle = getComputedStyle(document.documentElement)) {
      const customHeader = Number.parseInt(rootStyle.getPropertyValue('--sup-header-height'), 10);
      if (!Number.isNaN(customHeader)) return customHeader;

      if (!document.querySelector('store-header[data-is-sticky="true"]')) return 0;
      const themeHeader = Number.parseInt(rootStyle.getPropertyValue('--header-height'), 10);
      return Number.isNaN(themeHeader) ? 0 : themeHeader;
    }

    /** @returns {number} */
    get headerHeight() {
      return this.getHeaderHeight();
    }

    /**
     * Distance from the top of the viewport at which the element docks.
     *
     * The custom header publishes how much of the viewport it currently covers,
     * which is zero while it is scrolled out of view. Following that value lets
     * the column reclaim the strip instead of reserving space for a header that
     * is not on screen.
     * @returns {number}
     */
    get defaultTop() {
      const rootStyle = getComputedStyle(document.documentElement);
      const overlaid = rootStyle.getPropertyValue('--sup-header-offset').trim();

      // Pages using the theme's own header never publish the offset. There the
      // header height is constant, so reserving it outright is still correct.
      if (!overlaid) {
        const themeHeader = this.getHeaderHeight(rootStyle);
        return themeHeader ? themeHeader + 30 : 48;
      }

      const gap = Number.parseInt(rootStyle.getPropertyValue('--sup-sticky-gap'), 10);
      return (Number.parseInt(overlaid, 10) || 0) + (Number.isNaN(gap) ? 24 : gap);
    }

    /**
     * Either inits or destroys the component based on screen size
     */
    toggleListeners() {
      if ((this.minStickySize === 'lg' && theme.mediaMatches.lg)
        || (this.minStickySize === 'md' && theme.mediaMatches.md)) {
        this.addListeners();
      } else {
        this.removeListeners();
      }
    }

    /**
     * Init the scroll watching
     */
    addListeners() {
      this.scrollListener = this.scrollListener || this.handleScroll.bind(this);
      window.addEventListener('scroll', this.scrollListener);
      // The custom header hides and reveals itself as a consequence of the last
      // scroll event, so without its own signal the column would keep docking
      // against a header position that no longer applies.
      window.addEventListener('on:header-offset-change', this.scrollListener);

      this.scrollListener();

      // Set the height of the parent to the total height of other elements
      if (this.parentElement.dataset.stickyHeightElems) {
        this.debouncedSetStickyHeights = this.debouncedSetStickyHeights
          || debounce(this.setStickyHeight.bind(this), 500);
        window.addEventListener('resize', this.debouncedSetStickyHeights);

        if ('MutationObserver' in window) {
          this.stickyHeightElems = this.parentElement.dataset.stickyHeightElems;
          this.observer = new MutationObserver(this.debouncedSetStickyHeights);
          document.querySelectorAll(this.stickyHeightElems).forEach((elem) => {
            this.observer.observe(elem, {
              childList: true,
              attributes: true,
              subtree: true
            });
          });
        }

        this.setStickyHeight();
      }

      this.watchScrollLock();

      // Listen for the opening of any disclosures, so their contents can be scrolled to if needed
      this.disclosures = this.querySelectorAll('details');
      if (this.disclosures) {
        this.disclosureChangeHandler = this.disclosureChangeHandler
          || this.handleDisclosureChange.bind(this);
        this.disclosures.forEach((disclosure) => {
          disclosure.addEventListener('transitionend', this.disclosureChangeHandler);
        });
      }
    }

    /**
     * Re-positions the column once a drawer or modal releases the page.
     *
     * Closing one restores the scroll offset the document already sits at, so
     * no scroll event follows to trigger a recalculation, and the column would
     * keep the position it held before the lock. That is visible whenever the
     * drawer reveals the header, because the column then stays too high.
     */
    watchScrollLock() {
      if (!('MutationObserver' in window) || this.lockObserver) return;

      this.wasLocked = document.body.classList.contains('fixed');
      this.lockObserver = new MutationObserver(() => {
        const locked = document.body.classList.contains('fixed');
        if (locked === this.wasLocked) return;

        this.wasLocked = locked;
        if (!locked) this.handleScroll();
      });
      this.lockObserver.observe(document.body, { attributeFilter: ['class'] });
    }

    /**
     * Handle the opening/closing of disclosures when they extend beyond the viewport
     * @param {object} evt - Event object.
     */
    handleDisclosureChange(evt) {
      if (evt.target.classList.contains('disclosure__panel')) {
        const summaryElem = evt.target.closest('.disclosure').querySelector('summary');
        if (!theme.elementUtil.isInViewport(summaryElem)) {
          window.scrollTo({
            behavior: 'smooth',
            top: summaryElem.getBoundingClientRect().top
              - document.body.getBoundingClientRect().top - this.headerHeight - 20
          });
        }
      }
    }

    /**
     * Destroy listeners, reset css attributes
     */
    removeListeners() {
      this.container.style.top = '';
      window.removeEventListener('scroll', this.scrollListener);
      window.removeEventListener('on:header-offset-change', this.scrollListener);

      if (this.observer) {
        this.observer.disconnect();
        window.removeEventListener('resize', this.debouncedSetStickyHeights);
      }

      if (this.lockObserver) {
        this.lockObserver.disconnect();
        this.lockObserver = null;
      }

      if (this.disclosures) {
        this.disclosures.forEach((disclosure) => {
          disclosure.removeEventListener('transitionend', this.disclosureChangeHandler);
        });
      }
    }

    /**
     * Calculates the heights of certain elements that the height of the sticky element depends on
     */
    setStickyHeight() {
      let totalHeight = 0;
      document.querySelectorAll(this.stickyHeightElems).forEach((elem) => {
        totalHeight += elem.getBoundingClientRect().height;
      });
      this.parentElement.style.setProperty('--sticky-height', `${parseInt(totalHeight, 10)}px`);
    }

    /**
     * Updates the current css top based on scroll direction
     */
    handleScroll() {
      // Drawers and modals lock the page by making the body position: fixed and
      // offsetting it. window.scrollY then reads 0 while offsetTop still refers
      // to the unshifted document, and mixing the two throws the column far off
      // screen. A docked column still needs to follow the header, which drawers
      // reveal, but that position is a constant and needs none of the geometry.
      if (document.body.classList.contains('fixed')) {
        if (this.container.classList.contains('is-docked')) {
          this.currentTop = this.defaultTop;
          this.container.style.top = `${this.currentTop}px`;
        }
        return;
      }

      const { defaultTop } = this;
      const bounds = this.container.getBoundingClientRect();
      const maxTop = bounds.top + window.scrollY - this.container.offsetTop + defaultTop;
      const minTop = this.container.clientHeight - window.innerHeight;

      if (window.scrollY < this.scrollY) {
        this.currentTop -= window.scrollY - this.scrollY;
      } else {
        this.currentTop += this.scrollY - window.scrollY;
      }

      this.currentTop = Math.min(Math.max(this.currentTop, -minTop), maxTop, defaultTop);
      this.scrollY = window.scrollY;
      this.container.style.top = `${this.currentTop}px`;
      // Only while docked does top hold still between frames, so only then may
      // the change to it be animated.
      this.container.classList.toggle('is-docked', this.currentTop === defaultTop);
    }
  }

  customElements.define('sticky-scroll-direction', StickyScrollDirection);
}
